import React, { useState } from 'react';
import {
    Button, Typography, Container, Box, Divider, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import massageIcon from '../../assets/svgs-orig/001-body-massage.svg';
import beautyIcon from '../../assets/svgs-orig/005-makeover.svg';
import fitnessIcon from '../../assets/svgs-orig/008-fitness.svg';
import nutritionIcon from '../../assets/svgs-orig/013-health.svg';
import tutoringIcon from '../../assets/svgs-orig/tutor.svg';
import wealthIcon from '../../assets/svgs-orig/wealth.svg';

import {ReactComponent as MassageSvg} from "../../assets/svgs-orig/001-body-massage.svg";
import {ReactComponent as BeautySvg} from "../../assets/svgs-orig/005-makeover.svg";
import {ReactComponent as FitnessSvg} from "../../assets/svgs-orig/008-fitness.svg";
import {ReactComponent as NutritionSvg} from "../../assets/svgs-orig/013-health.svg";
import {ReactComponent as TutoringSvg} from "../../assets/svgs-orig/tutor.svg";
import {ReactComponent as WealthSvg} from "../../assets/svgs-orig/wealth.svg";

function PlannerStep1() {
  const navigate = useNavigate();
  const [selectedInterests, setSelectedInterests] = useState([]);
  
  // Demographic states
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [income, setIncome] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [preferredDays, setPreferredDays] = useState("");
  const [plan, setPlan] = useState("");
  const [setting, setSetting] = useState("");

  const interests = ['Massage', 'Beauty', 'Fitness', 'Nutrition', 'Tutoring', 'Wealth'];

  // Mapping of interest names to icon imports.
  const iconsMap = {
    Massage:  <MassageSvg width={"30"} height={"30"} strokeWidth={0} />,
    Beauty: <BeautySvg width={"30"} height={"30"} strokeWidth={0}/>,
    Fitness: <FitnessSvg width={"30"} height={"30"} strokeWidth={0}/>,
    Nutrition: <NutritionSvg width={"30"} height={"30"} strokeWidth={0}/>,
    Tutoring: <TutoringSvg width={"30"} height={"30"} strokeWidth={0}/>,
    Wealth: <WealthSvg width={"30"} height={"30"} strokeWidth={0} />,
  };

  // Toggle interests selection (multiple can be selected)
  const handleToggleInterest = (interest) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(item => item !== interest));
    } else {
      setSelectedInterests([...selectedInterests, interest]);
    }
  };

  const handleNext = () => {
    // Build query parameters for interests and demographics.
    const params = {
      interests: selectedInterests.map(item => item.toLowerCase()).join(";"),
      gender,
      age,
      income,
      preferredTime,
      preferredDays,
      plan,
      setting,
    };
    const queryString = new URLSearchParams(params).toString();
    navigate(`/plannerstep2?${queryString}`);
  };

  const handleDemographicSelection = (setState, option, value) => {
      if (value === option) {
          setState(null);
      } else{
          setState(option);
      }
  }

  // Render demographic row with fixed grid layout.
  // The width is set to a fixed 320px on mobile (xs) and 50% on medium and larger screens.
  const renderDemographicRow = (label, options, value, onChange) => {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 240px',
          alignItems: 'center',
          columnGap: 0.5,
          width: '320px',
          mb: 1,
        }}
      >
        <Typography 
          variant="body1" 
          sx={{ textAlign: 'left', justifySelf: 'start' }}
        >
          {label}
        </Typography>
          <ToggleButtonGroup value={value} sx={{
              display: 'grid',
              gridTemplateColumns: '80px 80px 80px',
              alignItems: 'center',
              columnGap: 0.5
          }}>


        {options.map(option => (
          <ToggleButton
            key={option}
            onClick={() => handleDemographicSelection(onChange,option,value)}
            value={option}
          >
            {option.split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
          </ToggleButton>
        ))}
          </ToggleButtonGroup>
      </Box>
    );
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        width: '100%',
        marginBottom: '50px',
        marginRight: 'auto',
        marginLeft: 'auto',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        textAlign: 'center', 
        mt: 4 
      }}
    >
      {/* Main Heading */}
      <Typography variant="h3" component="h1">
        Selfcare Planner
      </Typography>
      <Divider sx={{ width: '100%', my: 2, backgroundColor: 'gray' }} />

        {/* Interests Section */}
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Select Your Interests
        </Typography>
        <ToggleButtonGroup
            value={selectedInterests}
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                width: 'auto',
                placeItems: 'center',
            }}
        >
            {interests.map(interest => (
                <ToggleButton
                    key={interest}
                    value={interest}
                    onClick={() => handleToggleInterest(interest)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '60px',
                        height: '80px',
                        borderRadius: '25px !important',
                        margin: 'auto'
                    }}

                >
                    <svg
                        style={{ width: 30, height: 30, display: 'block', margin: '0 auto' }}
                        fill={selectedInterests.includes(interest)?'white':'black'}
                    >
                        {iconsMap[interest]}
                    </svg>
                    <Typography
                        variant="caption"
                        sx={{ mt: 0.5, textAlign: 'center' }}
                    >
                        {interest.split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                    </Typography>
                </ToggleButton>
            ))}
        </ToggleButtonGroup>

        <Divider sx={{ width: '100%', my: 2, backgroundColor: 'gray' }} />
      {/* Demographic Information Heading */}
      <Typography variant="h6" component="h2" sx={{ mb: 4 }}>
        Demography and Preferences
      </Typography>
      {/* Demographic Information Section */}
      {renderDemographicRow("Gender:", ["Male", "Female", "No Answer"], gender, setGender)}
      {renderDemographicRow("Age:", ["Young", "Mid-age", "Senior"], age, setAge)}
      {renderDemographicRow("Income:", ["Low", "Average", "High"], income, setIncome)}
      {renderDemographicRow("Time:", ["Any Time", "Morning", "Afternoon"], preferredTime, setPreferredTime)}
      {renderDemographicRow("Days:", ["All Days", "Weekdays", "Weekends"], preferredDays, setPreferredDays)}
      {renderDemographicRow("Plan:", ["weekly", "monthly", "Both"], plan, setPlan)}
      {renderDemographicRow("Setting:", ["solo", "group", "mixed"], setting, setSetting)}
      



      
      {/* Next Button */}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={selectedInterests.length === 0}
          onClick={handleNext}
        >
          Next
        </Button>
      </Box>
    </Container>
  );
}

export default PlannerStep1;